import { Youtube, AlertCircle } from "lucide-react";

/* Step 5 — Special Offer. Optional offer ribbon shown on the listing card
   and as a banner on the property page. When `enabled` is false the
   fields are dimmed but preserved so toggling on later doesn't lose
   anything. The YouTube link is fully optional and renders as a small
   icon button next to the offer on the customer site — mirroring the
   YouTube social icon already on the footer. */

/* Pull the 11-character video ID out of any common YouTube URL shape:
   - youtu.be/<id>
   - youtube.com/watch?v=<id>
   - youtube.com/shorts/<id>
   - youtube.com/embed/<id>
   - youtube.com/live/<id>
   Returns null when the URL isn't a YouTube URL — the SaveOffer step
   uses that to decide whether to render the thumbnail preview. */
function extractYouTubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url.trim());
    if (u.hostname === "youtu.be") {
      const id = u.pathname.slice(1).split("/")[0];
      return id || null;
    }
    if (
      u.hostname === "youtube.com" ||
      u.hostname === "www.youtube.com" ||
      u.hostname === "m.youtube.com" ||
      u.hostname === "youtube-nocookie.com" ||
      u.hostname === "www.youtube-nocookie.com"
    ) {
      const v = u.searchParams.get("v");
      if (v) return v;
      const parts = u.pathname.split("/").filter(Boolean);
      if (parts[0] === "shorts" && parts[1]) return parts[1];
      if (parts[0] === "embed" && parts[1]) return parts[1];
      if (parts[0] === "live" && parts[1]) return parts[1];
    }
  } catch {
    return null;
  }
  return null;
}

export function SpecialOfferStep({ data, setData }) {
  const so = data.specialOffer || { enabled: false, badge: "", title: "", discount: "", description: "", validTill: "", youtubeUrl: "" };
  const setSo = (patch) => setData({ ...data, specialOffer: { ...so, ...patch } });
  const youtubeUrl = so.youtubeUrl || "";
  const videoId = extractYouTubeId(youtubeUrl);

  const fieldCls = "w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed";
  const labelCls = "text-xs font-medium text-slate-600 mb-1.5 block";

  return (
    <div className="space-y-5">
      <div className="rounded-xl ring-1 ring-slate-200 bg-white p-4 flex items-start gap-4">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={so.enabled}
            onChange={(e) => setSo({ enabled: e.target.checked })}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c79a3a]" />
        </label>
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-900">
            {so.enabled ? "Special offer active" : "No special offer"}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            {so.enabled
              ? "A gold-ribbon banner will show on the listing card and the property detail page."
              : "Toggle on to show a promotional banner. You can fill in details now and enable it later."}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Offer badge</label>
          <input
            value={so.badge || ""}
            onChange={(e) => setSo({ badge: e.target.value })}
            placeholder="e.g. Limited time"
            disabled={!so.enabled}
            className={fieldCls}
          />
        </div>
        <div>
          <label className={labelCls}>Discount / benefit</label>
          <input
            value={so.discount || ""}
            onChange={(e) => setSo({ discount: e.target.value })}
            placeholder="e.g. Save up to ₹3 L"
            disabled={!so.enabled}
            className={fieldCls}
          />
        </div>
      </div>

      <div>
        <label className={labelCls}>Offer title</label>
        <input
          value={so.title}
          onChange={(e) => setSo({ title: e.target.value })}
          placeholder="e.g. Pre-launch Pricing, Festive Discount"
          disabled={!so.enabled}
          className={fieldCls}
        />
      </div>

      <div>
        <label className={labelCls}>Offer description</label>
        <textarea
          value={so.description}
          onChange={(e) => setSo({ description: e.target.value })}
          placeholder="Describe the offer in 1-2 sentences."
          disabled={!so.enabled}
          rows={3}
          className={fieldCls + " resize-y"}
        />
      </div>

      <div>
        <label className={labelCls}>Valid till</label>
        <input
          type="date"
          value={so.validTill}
          onChange={(e) => setSo({ validTill: e.target.value })}
          disabled={!so.enabled}
          className={fieldCls + " tabular-nums max-w-xs"}
        />
        <p className="text-[10px] text-slate-400 mt-1">
          After this date the banner auto-hides on the customer site (when the API integration lands).
        </p>
      </div>

      {/* YouTube link moved to the Media step (walkthrough video). Kept here
          disabled to avoid a large delete; never renders. */}
      {false && (
      <div className="pt-5 border-t border-slate-200">
        <label className={labelCls + " inline-flex items-center gap-1.5"}>
          <Youtube className="w-3.5 h-3.5 text-red-600" />
          YouTube video link <span className="text-slate-400 font-normal">(optional)</span>
        </label>
        <input
          type="url"
          value={youtubeUrl}
          onChange={(e) => setSo({ youtubeUrl: e.target.value })}
          placeholder="https://www.youtube.com/watch?v=…"
          disabled={!so.enabled}
          className={fieldCls}
        />
        <p className="text-[10px] text-slate-400 mt-1">
          Walk-through, builder pitch, drone footage, or testimonial. When set, a YouTube icon button appears next to the offer banner on the customer site, linking to this video.
        </p>

        {/* Thumbnail preview — only when a valid YouTube URL is entered.
            img.youtube.com is YouTube's public thumbnail CDN, no API key
            required. The play-circle overlay makes it obviously
            clickable; clicking opens the video in a new tab. */}
        {videoId && (
          <div className="mt-3 flex items-center gap-3 p-3 bg-white ring-1 ring-slate-200 rounded-xl">
            <a
              href={youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 relative group"
              title="Open in YouTube"
            >
              <img
                src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                alt="YouTube video thumbnail"
                className="w-36 h-20 rounded-md object-cover ring-1 ring-slate-300"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div className="absolute inset-0 grid place-items-center rounded-md transition-colors group-hover:bg-black/10">
                <div className="w-9 h-9 rounded-full bg-red-600 grid place-items-center shadow-md">
                  <Youtube className="w-4 h-4 text-white" fill="currentColor" />
                </div>
              </div>
            </a>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-slate-900">YouTube video detected</div>
              <div className="text-[11px] text-slate-500 font-mono truncate mt-0.5">id: {videoId}</div>
              <a
                href={youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-blue-700 hover:underline mt-1 inline-block"
              >
                Open in YouTube →
              </a>
            </div>
          </div>
        )}

        {/* Soft warning when something is in the field but doesn't parse
            as a YouTube URL. The link still saves — they might be
            linking to Vimeo, a Drive video, etc — but the customer-site
            YouTube icon won't render for non-YouTube URLs. */}
        {youtubeUrl && !videoId && (
          <div className="mt-2 inline-flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-md px-2 py-1.5">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              This doesn&apos;t look like a YouTube URL. The link will still save but the YouTube icon won&apos;t appear on the customer site.
            </span>
          </div>
        )}
      </div>
      )}
    </div>
  );
}